import { CheckTreeView, RenderTree } from '@components/CheckTreeView';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { TableLinearLoading } from '@components/Table/TableLinearLoading';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValueState } from '@hooks/useValueState';
import { IPermissao } from '@model/Permissao';
import { IUsuario } from '@model/Usuario';
import { IPermissionResponse, useUsuarioService } from '@services/UsuarioService';
import React from 'react';

export type ControlePermissaoProps = {
    modal: IModalWindow
    usuario: IUsuario
}

export function ControlePermissao ({ modal, usuario }: ControlePermissaoProps) {
    const data = useValueState<IPermissionResponse>();

    const loading = useLoading();
    const message = useMessage();

    const usuarioService = useUsuarioService();

    const createTreeItem = React.useCallback((permissao: IPermissao, permissions: IPermissao[]) : RenderTree => {
        return {
            id: permissao.permissaoId,
            name: permissao.descricao || permissao.codigo.split(".").pop() || `Código ${permissao.permissaoId}`,
            children: permissions.filter(x => x.codigo.indexOf(`${permissao.codigo}.`)).sort().map(x => createTreeItem(x, permissions))
        }
    }, []);

    const convertPermissionToRenderTree = React.useCallback((permissions: IPermissao[]) : RenderTree => {
        const roots = permissions.filter(x => x.codigo.indexOf(".") === -1).sort().map(x => createTreeItem(x, permissions));

        return {
            id: 0,
            name: "Permissões",
            children: roots
        }
    }, [createTreeItem])

    const handleEnter = React.useCallback(() => {
        if (!loading.isLoading) {
            usuarioService.listarPermissoes(usuario.usuarioId)
                .then(data.handleResponse)
                .catch(message.showException)
                .finally(loading.stop);
        }
    }, [data.handleResponse, loading.isLoading, loading.stop, message.showException, usuario.usuarioId, usuarioService]);

    return (
        <ModalWindow 
            modal={modal}
            size="lg"
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle
                title={`Permissões de ${usuario.nome}`}
            />

            <TableLinearLoading isLoading={loading.isLoading} />

            {
                data.value !== undefined && (
                    <CheckTreeView 
                        data={convertPermissionToRenderTree(data.value.allPermissions)}
                        currentSelected={data.value.userPermissions}
                    />
                )
            }
        </ModalWindow>
    )
}